import { useEffect, useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select, { type SelectOption } from "../../../components/ui/Select";
import { useGetProfileQuery } from "../../../store/api/authApi";
import { useListOrganizationsQuery } from "../../../store/api/organizationsApi";
import type { Property, PropertyData } from "../../../store/api/propertiesApi";
import {
	useCreatePropertyMutation,
	useUpdatePropertyMutation,
} from "../../../store/api/propertiesApi";
import type { RentType } from "../../../types";
import { formatCurrency } from "../../../utils/formatCurrency";

interface AddPropertyModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSuccess: () => void;
	property?: Property | null; // If provided, we're editing
}

const PROPERTY_TYPE_OPTIONS: SelectOption[] = [
	{ value: "apartment", label: "Apartment" },
	{ value: "house", label: "House" },
	{ value: "condo", label: "Condo" },
	{ value: "commercial", label: "Commercial" },
	{ value: "office", label: "Office" },
	{ value: "other", label: "Other" },
];

const STATUS_OPTIONS: SelectOption[] = [
	{ value: "available", label: "Available" },
	{ value: "pending", label: "Pending Deal" },
	{ value: "leased", label: "Leased" },
	{ value: "sold", label: "Sold" },
	{ value: "maintenance", label: "Maintenance" },
];

const NIGERIAN_STATES: SelectOption[] = [
	{ value: "Abia", label: "Abia" },
	{ value: "Adamawa", label: "Adamawa" },
	{ value: "Akwa Ibom", label: "Akwa Ibom" },
	{ value: "Anambra", label: "Anambra" },
	{ value: "Bauchi", label: "Bauchi" },
	{ value: "Bayelsa", label: "Bayelsa" },
	{ value: "Benue", label: "Benue" },
	{ value: "Borno", label: "Borno" },
	{ value: "Cross River", label: "Cross River" },
	{ value: "Delta", label: "Delta" },
	{ value: "Ebonyi", label: "Ebonyi" },
	{ value: "Edo", label: "Edo" },
	{ value: "Ekiti", label: "Ekiti" },
	{ value: "Enugu", label: "Enugu" },
	{ value: "FCT", label: "Federal Capital Territory" },
	{ value: "Gombe", label: "Gombe" },
	{ value: "Imo", label: "Imo" },
	{ value: "Jigawa", label: "Jigawa" },
	{ value: "Kaduna", label: "Kaduna" },
	{ value: "Kano", label: "Kano" },
	{ value: "Katsina", label: "Katsina" },
	{ value: "Kebbi", label: "Kebbi" },
	{ value: "Kogi", label: "Kogi" },
	{ value: "Kwara", label: "Kwara" },
	{ value: "Lagos", label: "Lagos" },
	{ value: "Nasarawa", label: "Nasarawa" },
	{ value: "Niger", label: "Niger" },
	{ value: "Ogun", label: "Ogun" },
	{ value: "Ondo", label: "Ondo" },
	{ value: "Osun", label: "Osun" },
	{ value: "Oyo", label: "Oyo" },
	{ value: "Plateau", label: "Plateau" },
	{ value: "Rivers", label: "Rivers" },
	{ value: "Sokoto", label: "Sokoto" },
	{ value: "Taraba", label: "Taraba" },
	{ value: "Yobe", label: "Yobe" },
	{ value: "Zamfara", label: "Zamfara" },
];

// Helper functions for rent calculations
const getRentLabel = (type?: RentType): string => {
	const labels: Record<RentType, string> = {
		annual: "per year",
		biannual: "per 6 months",
		monthly: "per month",
		weekly: "per week",
	};
	return labels[type || "annual"];
};

const calculateTotal = (
	amount: number,
	type: RentType,
	years: number,
): number => {
	const periodsPerYear: Record<RentType, number> = {
		annual: 1,
		biannual: 2,
		monthly: 12,
		weekly: 52,
	};
	return amount * periodsPerYear[type] * years;
};

const AddPropertyModal: React.FC<AddPropertyModalProps> = ({
	isOpen,
	onClose,
	onSuccess,
	property,
}) => {
	const { data: userProfile } = useGetProfileQuery();
	// Only fetch organizations if user is admin (skip query to avoid 500 errors for non-admins)
	const { data: organizationsData, error: organizationsError } =
		useListOrganizationsQuery(
			{
				page_size: 100,
			},
			{
				skip: userProfile?.role !== "admin",
			},
		);
	const [createProperty, { isLoading: isCreating }] =
		useCreatePropertyMutation();
	const [updateProperty, { isLoading: isUpdating }] =
		useUpdatePropertyMutation();

	const isEditMode = !!property;
	const isLoading = isCreating || isUpdating;

	const [formData, setFormData] = useState<Partial<PropertyData> & { listing_purpose?: "lease" | "sale" | "both" }>({
		organization: "",
		name: "",
		address: "",
		city: "",
		state: "",
		zip_code: "",
		unit_number: "",
		country: "Nigeria",
		property_type: "apartment",
		status: "available",
		bedrooms: undefined,
		bathrooms: undefined,
		square_feet: undefined,
		rent_amount: undefined,
		rent_type: "annual",
		initial_payment_duration: 1,
		monthly_rent: undefined,
		purchase_price: undefined,
		security_deposit: undefined,
		description: "",
		features: [],
		images: [],
		image_files: [],
		listing_purpose: undefined,
	});

	const [errors, setErrors] = useState<Record<string, string>>({});
	const [imagePreviews, setImagePreviews] = useState<string[]>([]);

	// Load property data if editing
	useEffect(() => {
		if (property && isOpen) {
			// Determine listing_purpose based on existing prices
			let listing_purpose: "lease" | "sale" | "both" | undefined = undefined;
			if (property.rent_amount && property.purchase_price) {
				listing_purpose = "both";
			} else if (property.rent_amount) {
				listing_purpose = "lease";
			} else if (property.purchase_price) {
				listing_purpose = "sale";
			}

			setFormData({
				organization: property.organization || "",
				name: property.name,
				address: property.address,
				city: property.city || "",
				state: property.state || "",
				zip_code: property.zip_code || "",
				unit_number: property.unit_number || "",
				country: property.country || "Nigeria",
				property_type: property.property_type,
				status: property.status,
				bedrooms: property.bedrooms,
				bathrooms: property.bathrooms,
				square_feet: property.square_feet,
				rent_amount: property.rent_amount,
				rent_type: property.rent_type || "annual",
				initial_payment_duration: property.initial_payment_duration || 1,
				monthly_rent: property.monthly_rent,
				purchase_price: property.purchase_price,
				security_deposit: property.security_deposit,
				description: property.description || "",
				features: property.features || [],
				images: property.images || [],
				image_files: [],
				listing_purpose: listing_purpose,
			});
			setImagePreviews(property.images || []);
		} else if (!property && isOpen) {
			// Reset form for new property
			setFormData({
				organization: "",
				name: "",
				address: "",
				city: "",
				state: "",
				zip_code: "",
				country: "Nigeria",
				property_type: "apartment",
				status: "available",
				bedrooms: undefined,
				bathrooms: undefined,
				square_feet: undefined,
				rent_amount: undefined,
				rent_type: "annual",
				initial_payment_duration: 1,
				monthly_rent: undefined,
				purchase_price: undefined,
				security_deposit: undefined,
				description: "",
				features: [],
				images: [],
				image_files: [],
				listing_purpose: undefined,
			});
			setImagePreviews([]);
		}
	}, [property, isOpen]);

	// Set default organization from user profile
	useEffect(() => {
		if (
			!isEditMode &&
			organizationsData?.results &&
			organizationsData.results.length > 0 &&
			!formData.organization
		) {
			// Use first organization as default (or match user's org if available)
			setFormData((prev) => ({
				...prev,
				organization: organizationsData.results[0].id,
			}));
		}
	}, [organizationsData, isEditMode]);

	const handleInputChange = (
		field: keyof PropertyData | "listing_purpose",
		value: string | number | undefined | string[],
	) => {
		setFormData((prev) => {
			const updated = { ...prev, [field]: value };

			// Clear irrelevant pricing fields when listing_purpose changes
			if (field === "listing_purpose") {
				if (value === "lease") {
					// Clear sale fields
					updated.purchase_price = undefined;
				} else if (value === "sale") {
					// Clear lease fields
					updated.rent_amount = undefined;
					updated.rent_type = "annual";
					updated.initial_payment_duration = 1;
					updated.security_deposit = undefined;
				}
				// If "both", keep all fields
			}

			return updated;
		});

		if (errors[field]) {
			setErrors((prev) => ({ ...prev, [field]: "" }));
		}
	};

	// Compress image before converting to base64
	const compressImage = (
		file: File,
		maxWidth = 1920,
		quality = 0.8,
	): Promise<string> => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = (e) => {
				const img = new Image();
				img.onload = () => {
					const canvas = document.createElement("canvas");
					let width = img.width;
					let height = img.height;

					// Resize if larger than maxWidth
					if (width > maxWidth) {
						height = (height * maxWidth) / width;
						width = maxWidth;
					}

					canvas.width = width;
					canvas.height = height;

					const ctx = canvas.getContext("2d");
					if (!ctx) {
						reject(new Error("Could not get canvas context"));
						return;
					}

					ctx.drawImage(img, 0, 0, width, height);
					const compressedBase64 = canvas.toDataURL("image/jpeg", quality);
					resolve(compressedBase64);
				};
				img.onerror = reject;
				img.src = e.target?.result as string;
			};
			reader.onerror = reject;
			reader.readAsDataURL(file);
		});
	};

	const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files;
		if (!files) return;

		// Process all files - only allow jpg, jpeg, png
		const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
		const allowedExtensions = [".jpg", ".jpeg", ".png"];

		const fileArray = Array.from(files).filter((file) => {
			const isValidType = allowedTypes.includes(file.type?.toLowerCase());
			const isValidExtension = allowedExtensions.some((ext) =>
				file.name?.toLowerCase().endsWith(ext),
			);
			return isValidType || isValidExtension;
		});

		// Process all images in parallel
		const imagePromises = fileArray.map(async (file) => {
			try {
				// Compress image before adding
				return await compressImage(file);
			} catch (error) {
				console.error("Error compressing image:", error);
				// Fallback to original if compression fails
				return new Promise<string>((resolve, reject) => {
					const reader = new FileReader();
					reader.onloadend = () => {
						resolve(reader.result as string);
					};
					reader.onerror = reject;
					reader.readAsDataURL(file);
				});
			}
		});

		// Wait for all images to be processed
		const processedImages = await Promise.all(imagePromises);

		// Update state with all processed images
		setImagePreviews((prev) => [...prev, ...processedImages]);
		setFormData((prev) => ({
			...prev,
			image_files: [...(prev.image_files || []), ...processedImages],
		}));
	};

	const handleRemoveImage = (index: number) => {
		setImagePreviews((prev) => prev.filter((_, i) => i !== index));
		setFormData((prev) => ({
			...prev,
			image_files: (prev.image_files || []).filter((_, i) => i !== index),
			images: (prev.images || []).filter((_, i) => i !== index),
		}));
	};

	const validate = (): boolean => {
		const newErrors: Record<string, string> = {};

		// Organization is optional - backend will use user's organization automatically
		// Only validate if organizations exist and user explicitly needs to select one
		// For now, organization is optional since backend auto-assigns from user's org

		if (!formData.name?.trim()) {
			newErrors.name = "Property name is required";
		}

		if (!formData.address?.trim()) {
			newErrors.address = "Address is required";
		}

		if (!formData.property_type) {
			newErrors.property_type = "Property type is required";
		}

		// Validate listing_purpose is selected
		if (!formData.listing_purpose) {
			newErrors.listing_purpose = "Please select a listing purpose";
		}

		// Validate rent_amount is provided if listing_purpose includes lease
		if (
			(formData.listing_purpose === "lease" ||
				formData.listing_purpose === "both") &&
			!formData.rent_amount
		) {
			newErrors.rent_amount = "Rent amount is required for lease listings";
		}

		// Validate purchase_price is provided if listing_purpose includes sale
		if (
			(formData.listing_purpose === "sale" ||
				formData.listing_purpose === "both") &&
			!formData.purchase_price
		) {
			newErrors.purchase_price =
				"Purchase price is required for sale listings";
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!validate()) {
			return;
		}

		setErrors({});

		try {
			// Filter out empty features before submitting
			const filteredFeatures = (formData.features || []).filter(
				(f) => f && f.trim().length > 0,
			);

			// Build property data based on listing_purpose
			// For edit mode, we need to explicitly clear irrelevant pricing fields
			const propertyData: any = {
				// Organization is optional - backend will use user's organization if not provided
				...(formData.organization && { organization: formData.organization }),
				name: formData.name!,
				address: formData.address!,
				city: formData.city,
				state: formData.state,
				zip_code: formData.zip_code,
				unit_number: formData.unit_number || null,
				country: formData.country || "Nigeria",
				property_type: formData.property_type!,
				status: formData.status,
				bedrooms: formData.bedrooms,
				bathrooms: formData.bathrooms,
				square_feet: formData.square_feet,
				description: formData.description,
				features: filteredFeatures,
				images: formData.images,
				image_files: formData.image_files,
				// Legacy field (for backward compatibility)
				monthly_rent: formData.monthly_rent,
			};

			// Add lease pricing fields if applicable, otherwise explicitly null them for edit mode
			if (formData.listing_purpose === "lease" || formData.listing_purpose === "both") {
				propertyData.rent_amount = formData.rent_amount;
				propertyData.rent_type = formData.rent_type;
				propertyData.initial_payment_duration = formData.initial_payment_duration;
				propertyData.security_deposit = formData.security_deposit;
			} else if (isEditMode) {
				// In edit mode, explicitly clear lease fields if not applicable
				propertyData.rent_amount = null;
				propertyData.rent_type = null;
				propertyData.initial_payment_duration = null;
				propertyData.security_deposit = null;
			}

			// Add sale pricing field if applicable, otherwise explicitly null it for edit mode
			if (formData.listing_purpose === "sale" || formData.listing_purpose === "both") {
				propertyData.purchase_price = formData.purchase_price;
			} else if (isEditMode) {
				// In edit mode, explicitly clear purchase price if not applicable
				propertyData.purchase_price = null;
			}

			if (isEditMode && property) {
				await updateProperty({ id: property.id, data: propertyData }).unwrap();
			} else {
				await createProperty(propertyData).unwrap();
			}

			onSuccess();
		} catch (error: any) {
			console.error("Property save error:", error);

			if (error?.data) {
				const errorData = error.data;
				const newErrors: Record<string, string> = {};

				Object.keys(errorData).forEach((key) => {
					if (Array.isArray(errorData[key])) {
						newErrors[key] = errorData[key][0];
					} else {
						newErrors[key] = errorData[key];
					}
				});

				setErrors(newErrors);
			} else {
				setErrors({
					submit: "Failed to save property. Please try again.",
				});
			}
		}
	};

	if (!isOpen) return null;

	const organizationOptions: SelectOption[] =
		organizationsData?.results.map((org) => ({
			value: org.id,
			label: org.name,
		})) || [];

	return (
		<>
			{/* Backdrop */}
			<div
				className="fixed inset-0 bg-black/50 z-1100 animate-fade-in"
				onClick={onClose}
			/>

			{/* Modal */}
			<div className="fixed inset-0 z-1200 flex items-center justify-center p-4">
				<div className="bg-card rounded-lg border border-border w-full max-w-3xl max-h-[90vh] overflow-hidden animate-scale-in">
					{/* Header */}
					<div className="flex items-center justify-between p-6 border-b border-border">
						<h2 className="text-xl font-semibold text-foreground">
							{isEditMode ? "Edit Property" : "Add New Property"}
						</h2>
						<Button variant="ghost" size="icon" onClick={onClose}>
							<Icon name="X" size={20} />
						</Button>
					</div>

					{/* Form */}
					<form
						onSubmit={handleSubmit}
						className="overflow-y-auto max-h-[calc(90vh-140px)]"
					>
						<div className="p-6 space-y-6">
							{/* Organization */}
							{organizationOptions.length > 0 && (
								<div>
									<label className="mb-2 block text-sm font-medium text-foreground">
										Organization
										<span className="text-xs text-muted-foreground ml-2">
											(Optional - will use your organization if not selected)
										</span>
									</label>
									<Select
										value={formData.organization || ""}
										options={[
											{ value: "", label: "Use my organization (default)" },
											...organizationOptions,
										]}
										onValueChange={(value) =>
											handleInputChange("organization", value as string)
										}
									/>
									{errors.organization && (
										<p className="text-sm text-destructive mt-1">
											{errors.organization}
										</p>
									)}
								</div>
							)}

							{/* Basic Information */}
							<div>
								<h3 className="text-lg font-medium text-foreground mb-4">
									Basic Information
								</h3>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<Input
										label="Property Name"
										type="text"
										placeholder="Enter property name"
										value={formData.name || ""}
										onChange={(e) => handleInputChange("name", e.target.value)}
										error={errors.name}
										required
									/>

									<div className="md:col-span-2">
										<Input
											label="Address"
											type="text"
											placeholder="Enter full property address"
											value={formData.address || ""}
											onChange={(e) =>
												handleInputChange("address", e.target.value)
											}
											error={errors.address}
											required
										/>
									</div>

									<div className="md:col-span-2">
										<Input
											label="Unit Number (Optional)"
											type="text"
											placeholder="e.g., Apt 3B, Unit 205, Suite 100"
											value={formData.unit_number || ""}
											onChange={(e) =>
												handleInputChange("unit_number", e.target.value)
											}
											error={errors.unit_number}
											maxLength={50}
										/>
									</div>

									<Input
										label="City"
										type="text"
										placeholder="Enter city"
										value={formData.city || ""}
										onChange={(e) => handleInputChange("city", e.target.value)}
										error={errors.city}
									/>

									<div>
										<label className="mb-2 block text-sm font-medium text-foreground">
											State
										</label>
										<Select
											value={formData.state || ""}
											options={[
												{ value: "", label: "Select a state" },
												...NIGERIAN_STATES,
											]}
											onValueChange={(value) =>
												handleInputChange("state", value as string)
											}
										/>
										{errors.state && (
											<p className="text-sm text-destructive mt-1">
												{errors.state}
											</p>
										)}
									</div>

									<Input
										label="ZIP Code"
										type="text"
										placeholder="Enter ZIP code"
										value={formData.zip_code || ""}
										onChange={(e) =>
											handleInputChange("zip_code", e.target.value)
										}
										error={errors.zip_code}
									/>

									<Input
										label="Country"
										type="text"
										placeholder="Enter country"
										value={formData.country || "Nigeria"}
										onChange={(e) =>
											handleInputChange("country", e.target.value)
										}
										error={errors.country}
									/>
								</div>
							</div>

							{/* Property Details */}
							<div>
								<h3 className="text-lg font-medium text-foreground mb-4">
									Property Details
								</h3>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div>
										<label className="mb-2 block text-sm font-medium text-foreground">
											Property Type <span className="text-destructive">*</span>
										</label>
										<Select
											value={formData.property_type || ""}
											options={PROPERTY_TYPE_OPTIONS}
											onValueChange={(value) =>
												handleInputChange("property_type", value as string)
											}
											required
										/>
										{errors.property_type && (
											<p className="text-sm text-destructive mt-1">
												{errors.property_type}
											</p>
										)}
									</div>

									<div>
										<label className="mb-2 block text-sm font-medium text-foreground">
											Status
										</label>
										<Select
											value={formData.status || "available"}
											options={STATUS_OPTIONS}
											onValueChange={(value) =>
												handleInputChange("status", value as string)
											}
										/>
									</div>

									<Input
										label="Bedrooms"
										type="number"
										placeholder="Number of bedrooms"
										value={formData.bedrooms?.toString() || ""}
										onChange={(e) =>
											handleInputChange(
												"bedrooms",
												e.target.value ? parseInt(e.target.value) : undefined,
											)
										}
										error={errors.bedrooms}
										min="0"
									/>

									<Input
										label="Bathrooms"
										type="number"
										placeholder="Number of bathrooms"
										value={formData.bathrooms?.toString() || ""}
										onChange={(e) =>
											handleInputChange(
												"bathrooms",
												e.target.value ? parseFloat(e.target.value) : undefined,
											)
										}
										error={errors.bathrooms}
										min="0"
										step="0.5"
									/>

									<Input
										label="Square Feet"
										type="number"
										placeholder="Square footage"
										value={formData.square_feet?.toString() || ""}
										onChange={(e) =>
											handleInputChange(
												"square_feet",
												e.target.value ? parseInt(e.target.value) : undefined,
											)
										}
										error={errors.square_feet}
										min="0"
									/>
								</div>
							</div>

							{/* Pricing */}
							<div>
								<h3 className="text-lg font-medium text-foreground mb-4">
									Pricing
								</h3>

								{/* Listing Purpose Selection */}
								<div className="mb-6">
									<label className="block text-sm font-medium text-foreground mb-3">
										Listing Purpose{" "}
										<span className="text-destructive">*</span>
									</label>
									<div className="grid grid-cols-1 md:grid-cols-3 gap-3">
										<button
											type="button"
											onClick={() => handleInputChange("listing_purpose", "lease")}
											className={`p-4 rounded-lg border-2 smooth-transition ${
												formData.listing_purpose === "lease"
													? "border-blue-600 bg-blue-50/10"
													: "border-border bg-card hover:border-blue-300"
											}`}
										>
											<Icon name="Key" size={24} className="mx-auto mb-2 text-blue-600" />
											<p className="font-semibold text-foreground">For Lease</p>
											<p className="text-xs text-muted-foreground mt-1">
												Rental only
											</p>
										</button>

										<button
											type="button"
											onClick={() => handleInputChange("listing_purpose", "sale")}
											className={`p-4 rounded-lg border-2 smooth-transition ${
												formData.listing_purpose === "sale"
													? "border-green-600 bg-green-50/10"
													: "border-border bg-card hover:border-green-300"
											}`}
										>
											<Icon name="Tag" size={24} className="mx-auto mb-2 text-green-600" />
											<p className="font-semibold text-foreground">For Sale</p>
											<p className="text-xs text-muted-foreground mt-1">
												Purchase only
											</p>
										</button>

										<button
											type="button"
											onClick={() => handleInputChange("listing_purpose", "both")}
											className={`p-4 rounded-lg border-2 smooth-transition ${
												formData.listing_purpose === "both"
													? "border-blue-600 bg-gradient-to-r from-blue-50/10 to-green-50/10"
													: "border-border bg-card hover:border-primary"
											}`}
										>
											<div className="flex justify-center gap-1 mb-2">
												<Icon name="Key" size={20} className="text-blue-600" />
												<Icon name="Tag" size={20} className="text-green-600" />
											</div>
											<p className="font-semibold text-foreground">Lease & Sale</p>
											<p className="text-xs text-muted-foreground mt-1">
												Both options
											</p>
										</button>
									</div>
									{errors.listing_purpose && (
										<p className="text-destructive text-sm mt-2">
											{errors.listing_purpose}
										</p>
									)}
								</div>

								{/* Lease Pricing Section - Only show if listing_purpose is "lease" or "both" */}
								{(formData.listing_purpose === "lease" ||
									formData.listing_purpose === "both") && (
									<div className="border border-blue-500/30 rounded-lg p-5 bg-blue-50/5 mb-4">
										<div className="flex items-center gap-2 mb-4">
											<Icon name="Key" size={20} className="text-blue-600" />
											<h4 className="text-md font-semibold text-foreground">
												Lease Pricing
											</h4>
										</div>

										<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
											{/* Rent Period Type */}
											<div>
												<label className="block text-sm font-medium text-foreground mb-2">
													Rent Period Type{" "}
													<span className="text-destructive">*</span>
												</label>
												<select
													value={formData.rent_type || "annual"}
													onChange={(e) =>
														handleInputChange(
															"rent_type",
															e.target.value as RentType,
														)
													}
													className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
												>
													<option value="annual">Annual</option>
													<option value="biannual">Biannual (6 months)</option>
													<option value="monthly">Monthly</option>
													<option value="weekly">Weekly</option>
												</select>
											</div>

											{/* Rent Amount */}
											<Input
												label={`Rent Amount (${getRentLabel(
													formData.rent_type as RentType,
												)})`}
												type="number"
												placeholder="0.00"
												value={formData.rent_amount?.toString() || ""}
												onChange={(e) =>
													handleInputChange(
														"rent_amount",
														e.target.value
															? parseFloat(e.target.value)
															: undefined,
													)
												}
												error={errors.rent_amount}
												min="0"
												step="0.01"
												required
											/>

											{/* Initial Payment Duration */}
											<div>
												<label className="block text-sm font-medium text-foreground mb-2">
													Initial Payment Duration{" "}
													<span className="text-destructive">*</span>
												</label>
												<select
													value={formData.initial_payment_duration || 1}
													onChange={(e) =>
														handleInputChange(
															"initial_payment_duration",
															parseInt(e.target.value),
														)
													}
													className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
												>
													<option value="1">1 Year</option>
													<option value="2">2 Years</option>
												</select>
											</div>

											{/* Security Deposit */}
											<Input
												label="Security Deposit"
												type="number"
												placeholder="0.00"
												value={formData.security_deposit?.toString() || ""}
												onChange={(e) =>
													handleInputChange(
														"security_deposit",
														e.target.value
															? parseFloat(e.target.value)
															: undefined,
													)
												}
												error={errors.security_deposit}
												min="0"
												step="0.01"
											/>
										</div>

										{/* Initial Payment Total Calculation */}
										{formData.rent_amount && (
											<div className="mt-4 p-3 bg-muted rounded-lg border border-border">
												<p className="text-sm text-foreground">
													<span className="font-medium">
														Initial Payment Total:
													</span>{" "}
													{formatCurrency(
														calculateTotal(
															formData.rent_amount,
															(formData.rent_type as RentType) || "annual",
															formData.initial_payment_duration || 1,
														),
														{ showAbbreviation: false },
													)}
												</p>
											</div>
										)}
									</div>
								)}

								{/* Sale Pricing Section - Only show if listing_purpose is "sale" or "both" */}
								{(formData.listing_purpose === "sale" ||
									formData.listing_purpose === "both") && (
									<div className="border border-green-500/30 rounded-lg p-5 bg-green-50/5 mb-4">
										<div className="flex items-center gap-2 mb-4">
											<Icon name="Tag" size={20} className="text-green-600" />
											<h4 className="text-md font-semibold text-foreground">
												Sale Pricing
											</h4>
										</div>

										<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
											{/* Purchase Price */}
											<Input
												label="Purchase Price"
												type="number"
												placeholder="0.00"
												value={formData.purchase_price?.toString() || ""}
												onChange={(e) =>
													handleInputChange(
														"purchase_price",
														e.target.value
															? parseFloat(e.target.value)
															: undefined,
													)
												}
												error={errors.purchase_price}
												min="0"
												step="0.01"
												required
											/>
										</div>
									</div>
								)}
							</div>

							{/* Description */}
							<div>
								<label className="block text-sm font-medium text-foreground mb-2">
									Description
								</label>
								<textarea
									value={formData.description || ""}
									onChange={(e) =>
										handleInputChange("description", e.target.value)
									}
									placeholder="Describe the property, its features, and location benefits..."
									rows={4}
									className={`w-full px-3 py-2 border rounded-lg bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring ${
										errors.description ? "border-error" : "border-border"
									}`}
								/>
								{errors.description && (
									<p className="mt-1 text-sm text-destructive">
										{errors.description}
									</p>
								)}
							</div>

							{/* Features */}
							<div>
								<div className="flex items-center justify-between mb-2">
									<label className="block text-sm font-medium text-foreground">
										Property Features
									</label>
									<Button
										type="button"
										variant="outline"
										size="sm"
										onClick={() => {
											const currentFeatures = formData.features || [];
											handleInputChange("features", [...currentFeatures, ""]);
										}}
										className="flex items-center gap-1"
									>
										<Icon name="Plus" size={14} />
										Add Feature
									</Button>
								</div>
								<div className="space-y-2">
									{(formData.features || []).length === 0 ? (
										<p className="text-sm text-muted-foreground py-2">
											No features added. Click "Add Feature" to add one.
										</p>
									) : (
										(formData.features || []).map((feature, index) => (
											<div key={index} className="flex items-center gap-2">
												<Input
													type="text"
													value={feature}
													onChange={(e) => {
														const currentFeatures = formData.features || [];
														const updatedFeatures = [...currentFeatures];
														updatedFeatures[index] = e.target.value;
														handleInputChange("features", updatedFeatures);
													}}
													placeholder="e.g., Swimming Pool, Gym, Parking Space"
													className="flex-1"
												/>
												<Button
													type="button"
													variant="ghost"
													size="icon"
													onClick={() => {
														const currentFeatures = formData.features || [];
														const updatedFeatures = currentFeatures.filter(
															(_, i) => i !== index,
														);
														handleInputChange("features", updatedFeatures);
													}}
													className="flex-shrink-0 text-destructive hover:text-destructive"
													aria-label="Remove feature"
												>
													<Icon name="X" size={16} />
												</Button>
											</div>
										))
									)}
								</div>
								{errors.features && (
									<p className="mt-1 text-sm text-destructive">
										{errors.features}
									</p>
								)}
							</div>

							{/* Images */}
							<div>
								<h3 className="text-lg font-medium text-foreground mb-4">
									Property Images
								</h3>
								<div className="space-y-4">
									<input
										type="file"
										accept="image/jpeg,image/jpg,image/png,.jpg,.jpeg,.png"
										multiple
										onChange={handleImageUpload}
										className="block w-full text-sm text-foreground file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
									/>

									{imagePreviews.length > 0 && (
										<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
											{imagePreviews.map((preview, index) => (
												<div key={index} className="relative group">
													<img
														src={preview}
														alt={`Property ${index + 1}`}
														className="w-full h-32 object-cover rounded-lg border border-border"
													/>
													<button
														type="button"
														onClick={() => handleRemoveImage(index)}
														className="absolute top-2 right-2 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
													>
														<Icon name="X" size={16} />
													</button>
												</div>
											))}
										</div>
									)}
								</div>
							</div>

							{errors.submit && (
								<div className="rounded-lg bg-destructive/20 border border-destructive/30 p-3 text-sm text-destructive">
									{errors.submit}
								</div>
							)}
						</div>

						{/* Footer */}
						<div className="flex items-center justify-end space-x-3 p-6 border-t border-border bg-muted/20">
							<Button
								type="button"
								variant="outline"
								onClick={onClose}
								disabled={isLoading}
							>
								Cancel
							</Button>
							<Button type="submit" disabled={isLoading}>
								{isLoading ? (
									<>
										<Icon
											name="Loader2"
											size={16}
											className="mr-2 animate-spin"
										/>
										{isEditMode ? "Updating..." : "Creating..."}
									</>
								) : (
									<>
										<Icon
											name={isEditMode ? "Save" : "Plus"}
											size={16}
											className="mr-2"
										/>
										{isEditMode ? "Update Property" : "Add Property"}
									</>
								)}
							</Button>
						</div>
					</form>
				</div>
			</div>
		</>
	);
};

export default AddPropertyModal;
